import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Space, 
  Typography, 
  message, 
  Popconfirm,
  Alert,
  Tag,
  Tooltip,
  Collapse,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  MessageOutlined,
  WarningOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import orgSmsTemplateService, { PLATFORM_DEFAULT_TEMPLATES } from '../services/orgSmsTemplateService';
import SMSSegmentIndicator from './SMSSegmentIndicator';

const {
  getOrgSmsTemplates,
  createOrgSmsTemplate,
  updateOrgSmsTemplate,
  deleteOrgSmsTemplate,
  validateTemplateMessage
} = orgSmsTemplateService;

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const SmsTemplateManager = () => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  
  // State management
  const [templates, setTemplates] = useState({ emergency: [], operational: [], general: [] });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [form] = Form.useForm();

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Load organization SMS templates
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const orgTemplates = await getOrgSmsTemplates(db, currentUser?.orgId);
      setTemplates(orgTemplates);
      console.log('📱 SMS Template Manager: Loaded templates', orgTemplates);
    } catch (error) {
      console.error('❌ SMS Template Manager: Error loading templates:', error);
      message.error('Failed to load SMS templates');
      // Set platform defaults as fallback
      setTemplates(PLATFORM_DEFAULT_TEMPLATES);
    } finally {
      setLoading(false);
    }
  };

  // Handle create/edit template
  const handleSaveTemplate = async (values) => {
    try {
      const templateData = {
        name: values.name,
        message: values.message,
        category: values.category
      };

      if (editingTemplate && !editingTemplate.isDefault) {
        // Update existing org template
        await updateOrgSmsTemplate(db, editingTemplate.id, templateData, currentUser.uid);
        message.success('Template updated successfully');
      } else {
        // Create new org template
        await createOrgSmsTemplate(db, currentUser.orgId, templateData, currentUser.uid);
        message.success('Template created successfully');
      }

      // Reload templates and close modal
      await loadTemplates();
      setModalVisible(false);
      setEditingTemplate(null);
      form.resetFields();

    } catch (error) {
      console.error('❌ SMS Template Manager: Error saving template:', error);
      message.error('Failed to save template');
    }
  };

  // Handle delete template
  const handleDeleteTemplate = async (template) => {
    try {
      await deleteOrgSmsTemplate(db, template.id, currentUser.uid);
      message.success('Template deleted successfully');
      await loadTemplates();
    } catch (error) {
      console.error('❌ SMS Template Manager: Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };

  // Open create modal
  const handleCreate = () => {
    setEditingTemplate(null);
    form.resetFields();
    form.setFieldsValue({ category: 'general' });
    setModalVisible(true);
  };

  // Open edit modal
  const handleEdit = (template) => {
    setEditingTemplate(template);
    form.setFieldsValue({
      name: template.name,
      message: template.message,
      category: template.category
    });
    setModalVisible(true);
  };

  // Create organization override of platform default
  const handleCreateOverride = (platformTemplate) => {
    setEditingTemplate(null); // This is a new template, not editing existing
    form.resetFields();
    form.setFieldsValue({
      name: `${platformTemplate.name} (Custom)`,
      message: platformTemplate.message,
      category: platformTemplate.category
    });
    setModalVisible(true);
  };

  // Open preview modal
  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setPreviewModalVisible(true);
  };

  // Get preview message with sample data
  const getPreviewMessage = (templateMessage) => {
    if (!templateMessage) return '';
    
    return templateMessage
      .replace(/{organisation_name}/g, currentUser?.organization?.name || 'Your Organization')
      .replace(/{organization_name}/g, currentUser?.organization?.name || 'Your Organization')
      .replace(/{site_name}/g, 'Sample Site Name')
      .replace(/{manager_name}/g, 'John Smith')
      .replace(/{incident_title}/g, 'Building Fire Emergency')
      .replace(/{location}/g, 'Level 5, Building A')
      .replace(/{timestamp}/g, new Date().toLocaleString())
      .replace(/{current_time}/g, new Date().toLocaleTimeString())
      .replace(/{current_date}/g, new Date().toLocaleDateString())
      .replace(/{status}/g, 'Active')
      .replace(/{severity}/g, 'High');
  };

  // Table columns for templates
  const getTableColumns = (category) => [
    {
      title: 'Template Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {record.isDefault && <Tag color="blue">Platform Default</Tag>}
          {record.isOrgSpecific && <Tag color="green">Organization</Tag>}
        </Space>
      )
    },
    {
      title: 'Message Preview',
      dataIndex: 'message',
      key: 'message',
      render: (text) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {text.length > 80 ? `${text.substring(0, 80)}...` : text}
        </Text>
      )
    },
    {
      title: 'Length',
      key: 'length',
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {record.message.length} chars
          {record.message.length > 160 && (
            <Text type="warning"> ({Math.ceil(record.message.length / 160)} segments)</Text>
          )}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Preview template">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handlePreview(record)}
              size="small"
            />
          </Tooltip>
          
          {/* Show Edit/Delete for org-specific templates */}
          {!record.isDefault && record.isOrgSpecific && (
            <>
              <Tooltip title="Edit organization template">
                <Button 
                  type="text" 
                  icon={<EditOutlined />} 
                  onClick={() => handleEdit(record)}
                  size="small"
                />
              </Tooltip>
              
              <Popconfirm
                title="Delete Template"
                description="Are you sure you want to delete this organization template?"
                onConfirm={() => handleDeleteTemplate(record)}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete organization template">
                  <Button 
                    type="text" 
                    icon={<DeleteOutlined />} 
                    danger
                    size="small"
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
          
          {/* Show "Create Override" for platform defaults */}
          {record.isDefault && (
            <Tooltip title="Create organization-specific version of this template">
              <Button 
                type="text" 
                icon={<PlusOutlined />} 
                onClick={() => handleCreateOverride(record)}
                size="small"
                style={{ color: '#39325b' }}
              >
                Override
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  // Real-time validation
  const [validation, setValidation] = useState({ isValid: true, errors: [], warnings: [] });
  const handleMessageChange = (e) => {
    const message = e.target.value;
    const validationResult = validateTemplateMessage(message);
    setValidation(validationResult);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              📱 SMS Quick Templates
            </Title>
            <Text type="secondary">
              Customize SMS templates for your organization. These will appear in all SMS composers.
            </Text>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreate}
            style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
          >
            Add Template
          </Button>
        </div>

        <Alert
          message="🎯 How SMS Template Management Works"
          description={
            <div>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Platform Defaults:</strong> Blue-tagged templates are system-wide defaults available to all organizations. Click "Override" to create your custom version.
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Organization Templates:</strong> Green-tagged templates are your custom templates. You can fully edit and delete these.
              </p>
              <p style={{ margin: '0' }}>
                <strong>Variables:</strong> Use {'{organisation_name}'}, {'{site_name}'}, {'{timestamp}'}, etc. in your templates.
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      </div>

      {/* Templates by Category */}
      <Collapse defaultActiveKey={['emergency', 'operational', 'general']}>
        {Object.entries(templates).map(([category, categoryTemplates]) => (
          <Panel 
            header={
              <Space>
                <MessageOutlined />
                <Text strong style={{ textTransform: 'capitalize' }}>{category} Templates</Text>
                <Tag>{categoryTemplates.length}</Tag>
              </Space>
            } 
            key={category}
          >
            <Table
              dataSource={categoryTemplates}
              columns={getTableColumns(category)}
              loading={loading}
              pagination={false}
              size="small"
              rowKey="id"
              locale={{ emptyText: `No ${category} templates available` }}
            />
          </Panel>
        ))}
      </Collapse>

      {/* Create/Edit Template Modal */}
      <Modal
        title={
          <Space>
            <MessageOutlined />
            <span>{editingTemplate ? 'Edit SMS Template' : 'Create SMS Template'}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingTemplate(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveTemplate}
        >
          <Form.Item
            name="name"
            label="Template Name"
            rules={[
              { required: true, message: 'Please enter a template name' },
              { max: 50, message: 'Template name cannot exceed 50 characters' }
            ]}
          >
            <Input placeholder="e.g., Emergency Evacuation Alert" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select a category">
              <Option value="emergency">Emergency</Option>
              <Option value="operational">Operational</Option>
              <Option value="general">General</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="message"
            label="Template Message"
            rules={[
              { required: true, message: 'Please enter the template message' }
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Enter your SMS template message. Use variables like {organisation_name}, {site_name}, {timestamp}, etc."
              onChange={handleMessageChange}
            />
          </Form.Item>

          {/* Real-time validation */}
          <SMSSegmentIndicator message={form.getFieldValue('message') || ''} />

          {validation.errors.length > 0 && (
            <Alert
              type="error"
              message="Template Errors"
              description={
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {validation.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              style={{ marginBottom: '16px' }}
            />
          )}

          {validation.warnings.length > 0 && (
            <Alert
              type="warning"
              message="Template Warnings"
              description={
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {validation.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              }
              style={{ marginBottom: '16px' }}
            />
          )}

          {/* Available Variables */}
          <Card size="small" style={{ marginBottom: '16px' }}>
            <Text strong>Available Variables:</Text>
            <div style={{ marginTop: '8px' }}>
              {[
                '{organisation_name}',
                '{site_name}',
                '{manager_name}',
                '{incident_title}',
                '{location}',
                '{timestamp}',
                '{current_time}',
                '{current_date}',
                '{status}',
                '{severity}'
              ].map(variable => (
                <Tag key={variable} style={{ margin: '2px' }}>{variable}</Tag>
              ))}
            </div>
          </Card>

          {/* Preview */}
          {form.getFieldValue('message') && (
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Text strong>Preview with Sample Data:</Text>
              <div style={{ 
                marginTop: '8px', 
                padding: '8px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px'
              }}>
                {getPreviewMessage(form.getFieldValue('message'))}
              </div>
            </Card>
          )}

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                disabled={!validation.isValid}
                style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
              >
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Template Preview</span>
          </Space>
        }
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {previewTemplate && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Template: </Text>
              <Text>{previewTemplate.name}</Text>
              {previewTemplate.isDefault && <Tag color="blue" style={{ marginLeft: '8px' }}>Platform Default</Tag>}
              {previewTemplate.isOrgSpecific && <Tag color="green" style={{ marginLeft: '8px' }}>Organization</Tag>}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Category: </Text>
              <Tag style={{ textTransform: 'capitalize' }}>{previewTemplate.category}</Tag>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Original Template:</Text>
              <div style={{ 
                marginTop: '8px', 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                border: '1px solid #d9d9d9'
              }}>
                {previewTemplate.message}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Preview with Sample Data:</Text>
              <div style={{ 
                marginTop: '8px', 
                padding: '12px', 
                backgroundColor: '#e6f7ff', 
                borderRadius: '4px',
                fontSize: '14px',
                border: '1px solid #91d5ff'
              }}>
                {getPreviewMessage(previewTemplate.message)}
              </div>
            </div>

            <SMSSegmentIndicator message={previewTemplate.message} />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SmsTemplateManager; 